import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Timer hook with drift correction.
 *
 * Supports both countdown and stopwatch modes.
 * Uses Date.now() delta comparison to prevent drift over long sessions.
 *
 * @param {Object} options
 * @param {number} options.initialSeconds - Starting seconds (countdown) or 0 (stopwatch)
 * @param {'countdown'|'stopwatch'} options.mode - Timer direction
 * @param {Function} options.onExpire - Called when countdown hits 0
 * @param {number[]} options.warningThresholds - Seconds remaining that trigger onWarning
 * @param {Function} options.onWarning - Called with remaining seconds when threshold crossed
 */
export default function useTimer({
  initialSeconds = 0,
  mode = 'countdown',
  onExpire,
  warningThresholds = [],
  onWarning,
} = {}) {
  const [seconds, setSeconds] = useState(initialSeconds);
  const [isRunning, setIsRunning] = useState(false);
  const startTimeRef = useRef(null);
  const elapsedAtPauseRef = useRef(0);
  const intervalRef = useRef(null);
  const triggeredWarningsRef = useRef(new Set());

  const tick = useCallback(() => {
    if (!startTimeRef.current) return;

    const elapsed = elapsedAtPauseRef.current + (Date.now() - startTimeRef.current) / 1000;

    if (mode === 'countdown') {
      const remaining = Math.max(0, initialSeconds - elapsed);
      setSeconds(Math.ceil(remaining));

      // Check warning thresholds
      for (const threshold of warningThresholds) {
        if (remaining <= threshold && !triggeredWarningsRef.current.has(threshold)) {
          triggeredWarningsRef.current.add(threshold);
          onWarning?.(threshold);
        }
      }

      if (remaining <= 0) {
        clearInterval(intervalRef.current);
        setIsRunning(false);
        onExpire?.();
      }
    } else {
      setSeconds(Math.floor(elapsed));
    }
  }, [initialSeconds, mode, onExpire, warningThresholds, onWarning]);

  const start = useCallback(() => {
    if (isRunning) return;
    startTimeRef.current = Date.now();
    setIsRunning(true);
    intervalRef.current = setInterval(tick, 250); // 4x per second for smooth display
  }, [isRunning, tick]);

  const pause = useCallback(() => {
    if (!isRunning) return;
    elapsedAtPauseRef.current += (Date.now() - startTimeRef.current) / 1000;
    startTimeRef.current = null;
    clearInterval(intervalRef.current);
    setIsRunning(false);
  }, [isRunning]);

  const reset = useCallback((newInitialSeconds) => {
    clearInterval(intervalRef.current);
    startTimeRef.current = null;
    elapsedAtPauseRef.current = 0;
    triggeredWarningsRef.current = new Set();
    setIsRunning(false);
    setSeconds(newInitialSeconds ?? initialSeconds);
  }, [initialSeconds]);

  useEffect(() => {
    return () => clearInterval(intervalRef.current);
  }, []);

  const formatTime = useCallback((secs) => {
    const s = secs ?? seconds;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    return `${m}:${String(sec).padStart(2, '0')}`;
  }, [seconds]);

  return {
    seconds,
    isRunning,
    start,
    pause,
    reset,
    formatted: formatTime(),
    formatTime,
  };
}
